// Admin/Invoices/AddInvoice/AddInvoice.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Classes from "./AddInvoice.module.css";
import SelectDropdown from "./Dropdown/SelectDropdown";
import backend from "../../../../../../api";
import { useSelector } from "react-redux";

const LeftArrow = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" {...props} viewBox="0 0 448 512">
    <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z" />
  </svg>
);

const num = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const money = (n, c = "USD") =>
  Number(n || 0).toLocaleString(undefined, {
    style: "currency",
    currency: String(c || "USD").toUpperCase(),
  });

/** Row types:
 *  - fuel: uses service prices by fuelTypeId ('reg'|'mid'|'prem'); qty = gallons
 *          now supports per-row override (useOverride + overrideUnitPrice)
 *  - custom: arbitrary unitPrice * quantity
 *  - discount: negative unitPrice (quantity default 1)
 */
function newFuelRow() {
  return {
    kind: "fuel",
    description: "Fuel",
    fuelTypeId: "reg",
    unitPrice: 0,
    quantity: 1,
    amount: 0,
    meta: {},
    useOverride: false,
    overrideUnitPrice: "",
  };
}
function newCustomRow() {
  return {
    kind: "custom",
    description: "",
    unitPrice: 0,
    quantity: 1,
    amount: 0,
    meta: {},
  };
}
function newDiscountRow() {
  return {
    kind: "discount",
    description: "Discount",
    unitPrice: -5,
    quantity: 1,
    amount: -5,
    meta: {},
  };
}

export default function AddInvoice() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigateTo = useNavigate();

  const companyId =
    useSelector((s) => s?.companyInfo?._id) || "68120023c24987567a7249fe";

  const [clients, setClients] = useState([]);
  const [service, setService] = useState(null);
  const [selectedClient, setSelectedClient] = useState("");
  const [currencyCode, setCurrencyCode] = useState("usd");
  const [dueDate, setDueDate] = useState("");
  const [title, setTitle] = useState("Mobile Fuel Delivery");

  const [rows, setRows] = useState([newFuelRow()]);
  const [includeDelivery, setIncludeDelivery] = useState(true);
  const [includeEmergency, setIncludeEmergency] = useState(false);

  const [deliveryFeeAmount, setDeliveryFeeAmount] = useState(null);
  const [emergencyFeeAmount, setEmergencyFeeAmount] = useState(null);

  const [loading, setLoading] = useState(false);

  // NEW: payment + customer types
  // paymentType: 'cash' | 'online'
  const [paymentType, setPaymentType] = useState("cash");
  // customerType: 'registered' | 'guest' (not registered)
  const [customerType, setCustomerType] = useState("registered");

  // Guest contact fields (used only when customerType === 'guest')
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");

  // Fetch clients
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await backend.get("/api/user/by-company", {
          params: { companyId, role: "customer" },
        });
        if (alive && data?.ok) setClients(data.items || []);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [companyId]);

  // Fetch service (prices & fees)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!companyId) return;
        const { data } = await backend.get(
          `/api/services/by-company/${companyId}`
        );
        if (alive && data?.ok) setService(data.data || null);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [companyId]);

  // If editing, load invoice into form
  useEffect(() => {
    if (!isEdit) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await backend.get(`/api/invoices/${id}`);
        if (!alive || !data?.ok) return;
        const inv = data.invoice;

        const invUserId = inv.userId?._id || inv.userId || "";

        setSelectedClient(invUserId);
        setCurrencyCode(inv.currency || "usd");
        setDueDate(
          inv.dueAt ? new Date(inv.dueAt).toISOString().slice(0, 10) : ""
        );
        setTitle(inv.title || "Mobile Fuel Delivery");

        // --- Map old invoices into new schema ---
        let invPaymentType = inv.invoiceType || "cash"; // old might be 'cash', 'online', 'registered', 'guest'
        let invCustomerType = inv.customerType; // new field

        if (!invCustomerType) {
          // Backwards compatibility
          if (invPaymentType === "registered") {
            invCustomerType = "registered";
            invPaymentType = "cash";
          } else if (invPaymentType === "guest") {
            invCustomerType = "guest";
            invPaymentType = "cash";
          } else {
            invCustomerType = invUserId ? "registered" : "guest";
          }
        }

        setPaymentType(invPaymentType === "online" ? "online" : "cash");
        setCustomerType(invCustomerType === "guest" ? "guest" : "registered");

        setGuestName(inv.guestName || "");
        setGuestEmail(inv.guestEmail || "");
        setGuestPhone(inv.guestPhone || "");

        // build rows from lines (mark fee lines by description)
        const mapped = (inv.lines || []).map((li) => {
          const desc = li.description || "";
          const isDelivery = /^delivery fee$/i.test(desc);
          const isEmergency = /^emergency fee$/i.test(desc);
          if (isDelivery || isEmergency) {
            return {
              kind: "custom",
              description: li.description,
              unitPrice: num(li.unitPrice),
              quantity: num(li.quantity, 1),
              amount: num(li.amount),
              meta: { ...(li.meta || {}), isFee: true },
            };
          }
          if (li.fuelTypeId && ["reg", "mid", "prem"].includes(li.fuelTypeId)) {
            return {
              kind: "fuel",
              description: li.description || "Fuel",
              fuelTypeId: li.fuelTypeId,
              useOverride: true,
              overrideUnitPrice: num(li.unitPrice),
              unitPrice: num(li.unitPrice),
              quantity: num(li.quantity, 1),
              amount: num(li.amount),
              meta: li.meta || {},
            };
          }
          return {
            kind: num(li.unitPrice) < 0 ? "discount" : "custom",
            description: desc,
            unitPrice: num(li.unitPrice),
            quantity: num(li.quantity, 1),
            amount: num(li.amount),
            meta: li.meta || {},
          };
        });
        setRows(mapped.length ? mapped : [newFuelRow()]);

        const del = num(inv.deliveryFee);
        const emer = num(inv.emergencyFee);
        setIncludeDelivery(del > 0);
        setIncludeEmergency(emer > 0);
        setDeliveryFeeAmount(del || null);
        setEmergencyFeeAmount(emer || null);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, isEdit]);

  // Build client dropdown options
  const clientOptions = useMemo(
    () =>
      (clients || []).map((u) => ({
        value: u._id,
        label:
          u.name ||
          [u.firstName, u.lastName].filter(Boolean).join(" ") ||
          u.email ||
          "Unnamed",
        sublabel: u.email,
      })),
    [clients]
  );

  // price map from service (defaults)
  const priceMap = useMemo(
    () => ({
      reg: num(service?.prices?.regular),
      mid: num(service?.prices?.mid),
      prem: num(service?.prices?.premium),
    }),
    [service]
  );

  // default fees from service
  const defaultDelivery = useMemo(
    () =>
      num(
        service?.fees?.delivery ??
        service?.prices?.deliveryFee ??
        9.99
      ),
    [service]
  );
  const defaultEmergency = useMemo(
    () =>
      num(
        service?.fees?.emergency ??
        service?.prices?.emergencyFee ??
        0
      ),
    [service]
  );

  // seed fee amounts with defaults when toggled on (if not already set)
  useEffect(() => {
    if (includeDelivery && deliveryFeeAmount == null)
      setDeliveryFeeAmount(defaultDelivery);
  }, [includeDelivery, defaultDelivery, deliveryFeeAmount]);

  useEffect(() => {
    if (includeEmergency && emergencyFeeAmount == null)
      setEmergencyFeeAmount(defaultEmergency);
  }, [includeEmergency, defaultEmergency, emergencyFeeAmount]);

  // effective fees (0 if unchecked)
  const deliveryFee = includeDelivery
    ? num(deliveryFeeAmount, defaultDelivery)
    : 0;
  const emergencyFee = includeEmergency
    ? num(emergencyFeeAmount, defaultEmergency)
    : 0;

  // recalc row amounts (respect overrides for fuel rows)
  const computedRows = useMemo(() => {
    return rows.map((r) => {
      if (r.kind === "fuel") {
        const unit = r.useOverride
          ? num(r.overrideUnitPrice, 0)
          : priceMap[r.fuelTypeId || "reg"] ?? 0;
        const qty = num(r.quantity, 1);
        return {
          ...r,
          unitPrice: unit,
          amount: Number((unit * qty).toFixed(2)),
        };
      }
      if (r.kind === "discount") {
        const unit = -Math.abs(num(r.unitPrice, 0));
        const qty = num(r.quantity, 1);
        return {
          ...r,
          unitPrice: unit,
          amount: Number((unit * qty).toFixed(2)),
        };
      }
      const unit = num(r.unitPrice, 0);
      const qty = num(r.quantity, 1);
      return {
        ...r,
        amount: Number((unit * qty).toFixed(2)),
      };
    });
  }, [rows, priceMap]);

  const subtotal = useMemo(
    () =>
      Number(
        computedRows
          .filter((r) => !r.meta?.isFee)
          .reduce((s, r) => s + num(r.amount), 0)
          .toFixed(2)
      ),
    [computedRows]
  );

  const total = useMemo(
    () => Number((subtotal + deliveryFee + emergencyFee).toFixed(2)),
    [subtotal, deliveryFee, emergencyFee]
  );

  // CRUD helpers
  const addRow = (kind) => {
    setRows((rs) => [
      ...rs,
      kind === "custom"
        ? newCustomRow()
        : kind === "discount"
          ? newDiscountRow()
          : newFuelRow(),
    ]);
  };
  const updateRow = (i, patch) => {
    setRows((rs) =>
      rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r))
    );
  };
  const removeRow = (i) => {
    setRows((rs) => rs.filter((_, idx) => idx !== i));
  };

  const isRegisteredCustomer = customerType === "registered";
  const showClientDropdown = isRegisteredCustomer;
  const showGuestInputs = !isRegisteredCustomer;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (isRegisteredCustomer && !selectedClient) {
      return alert("Please select a registered client for this invoice.");
    }

    if (!isRegisteredCustomer && !guestEmail && !guestPhone) {
      return alert(
        "For not registered users, please enter at least email or phone."
      );
    }

    // Build lines
    const lines = computedRows.map((r) => ({
      description:
        r.kind === "fuel"
          ? r.description?.trim() || `Fuel (${r.fuelTypeId})`
          : r.description?.trim() ||
          (r.kind === "discount" ? "Discount" : "Custom Item"),
      unitPrice: num(r.unitPrice),
      quantity: num(r.quantity, 1),
      amount: num(
        r.amount,
        num(r.unitPrice) * num(r.quantity, 1)
      ),
      fuelTypeId: r.kind === "fuel" ? r.fuelTypeId : undefined,
      meta: {
        ...(r.meta || {}),
        isFee: r.meta?.isFee === true,
      },
    }));

    const feeLines = [];
    if (deliveryFee)
      feeLines.push({
        description: "Delivery Fee",
        unitPrice: deliveryFee,
        quantity: 1,
        amount: deliveryFee,
        meta: { isFee: true },
      });
    if (emergencyFee)
      feeLines.push({
        description: "Emergency Fee",
        unitPrice: emergencyFee,
        quantity: 1,
        amount: emergencyFee,
        meta: { isFee: true },
      });

    const payload = {
      // invoiceType now = payment method
      invoiceType: paymentType, // 'cash' or 'online'
      customerType, // 'registered' or 'guest'
      userId: isRegisteredCustomer ? selectedClient : undefined,
      companyId,
      currency: currencyCode || "usd",
      title,
      dueAt: dueDate || null,
      lines: [...lines, ...feeLines],
      deliveryFee,
      emergencyFee,

      // guest contact only for not registered
      guestName: !isRegisteredCustomer ? guestName || undefined : undefined,
      guestEmail: !isRegisteredCustomer ? guestEmail || undefined : undefined,
      guestPhone: !isRegisteredCustomer ? guestPhone || undefined : undefined,
    };

    try {
      setLoading(true);
      if (isEdit) {
        const { data } = await backend.put(
          `/api/invoices/${id}`,
          payload
        );
        if (data?.ok) navigateTo(`/view-invoice/${id}`);
        else alert(data?.error || "Update failed");
      } else {
        const { data } = await backend.post(
          `/api/invoices`,
          payload
        );
        if (data?.ok)
          navigateTo(`/view-invoice/${data.invoice._id}`);
        else alert(data?.error || "Create failed");
      }
    } catch (e2) {
      alert(e2?.response?.data?.error || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={Classes.hero}>
      <div className={Classes.body}>
        <div className={Classes.header}>
          <button
            onClick={() => navigateTo(-1)}
            className={Classes.backButton}
          >
            <LeftArrow height="13px" fill="#969696" />
            <h5>Go back</h5>
          </button>
        </div>

        <div className={Classes.hero2}>
          <h3>{isEdit ? "Edit invoice" : "Add invoice"}</h3>

          <form onSubmit={handleSubmit}>
            {/* Payment Type */}
            <div className={Classes.flexColGroup}>
              <div className={Classes.flexCol1}>
                <label className={Classes.label}>Payment type</label>
                <div
                  className="flexAlign10"
                  style={{ gap: 10, marginTop: 6 }}
                >
                  <button
                    type="button"
                    className={`${Classes.typeBtn} ${paymentType === "cash"
                        ? Classes.typeBtnActive
                        : ""
                      }`}
                    onClick={() => setPaymentType("cash")}
                  >
                    Cash payment
                  </button>
                  <button
                    type="button"
                    className={`${Classes.typeBtn} ${paymentType === "online"
                        ? Classes.typeBtnActive
                        : ""
                      }`}
                    onClick={() => setPaymentType("online")}
                  >
                    Online payment
                  </button>
                </div>
              </div>
            </div>

            {/* Customer Type */}
            <div className={Classes.flexColGroup}>
              <div className={Classes.flexCol1}>
                <label className={Classes.label}>Customer type</label>
                <div
                  className="flexAlign10"
                  style={{ gap: 10, marginTop: 6 }}
                >
                  <button
                    type="button"
                    className={`${Classes.typeBtn} ${customerType === "registered"
                        ? Classes.typeBtnActive
                        : ""
                      }`}
                    onClick={() => {
                      setCustomerType("registered");
                    }}
                  >
                    Registered
                  </button>
                  <button
                    type="button"
                    className={`${Classes.typeBtn} ${customerType === "guest"
                        ? Classes.typeBtnActive
                        : ""
                      }`}
                    onClick={() => {
                      setCustomerType("guest");
                      setSelectedClient("");
                    }}
                  >
                    Not registered
                  </button>
                </div>
              </div>
            </div>

            {/* Client + Due date */}
            <div className={Classes.flexColGroup}>
              {showClientDropdown && (
                <div className={Classes.flexCol1}>
                  <div className="flexAlign10">
                    <label className={Classes.label}>Client</label>
                    <div className={Classes.optionalText}>
                      <h5>Required</h5>
                    </div>
                  </div>
                  <SelectDropdown
                    options={clientOptions}
                    selected={selectedClient}
                    onChange={setSelectedClient}
                    placeholder="Select Client"
                    multiple={false}
                    searchPlaceholder="Search clients"
                  />
                </div>
              )}

              <div className={Classes.flexCol1}>
                <label className={Classes.label}>Due date</label>
                <input
                  type="date"
                  className={Classes.inputField}
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>
            </div>

            {/* Guest / Not registered contact fields */}
            {showGuestInputs && (
              <>
                <div className={Classes.flexColGroup}>
                  <div className={Classes.flexCol1}>
                    <label className={Classes.label}>
                      Customer name (optional)
                    </label>
                    <input
                      className={Classes.inputField}
                      value={guestName}
                      onChange={(e) =>
                        setGuestName(e.target.value)
                      }
                      placeholder="Walk-in / Guest customer name"
                    />
                  </div>
                  <div className={Classes.flexCol1}>
                    <label className={Classes.label}>
                      Email <span>(email or phone required)</span>
                    </label>
                    <input
                      className={Classes.inputField}
                      value={guestEmail}
                      onChange={(e) =>
                        setGuestEmail(e.target.value)
                      }
                      placeholder="customer@example.com"
                    />
                  </div>
                </div>

                <div className={Classes.flexColGroup}>
                  <div className={Classes.flexCol1}>
                    <label className={Classes.label}>
                      Phone <span>(email or phone required)</span>
                    </label>
                    <input
                      className={Classes.inputField}
                      value={guestPhone}
                      onChange={(e) =>
                        setGuestPhone(e.target.value)
                      }
                      placeholder="+1 555 000 0000"
                    />
                  </div>
                </div>
              </>
            )}

            <div className={Classes.flexColGroup}>
              <div className={Classes.flexCol1}>
                <label className={Classes.label}>Title</label>
                <input
                  className={Classes.inputField}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              {/* <div className={Classes.flexCol1}>
                <label className={Classes.label}>Currency</label>
                <input
                  className={Classes.inputField}
                  value={currencyCode}
                  onChange={(e) =>
                    setCurrencyCode(e.target.value)
                  }
                  placeholder="usd"
                />
              </div> */}
            </div>

            {/* Line items */}
            <div className={Classes.flexCol1}>
              <label className={Classes.label}>Line items</label>

              {computedRows.map((r, i) => (
                <div
                  key={i}
                  className={Classes.lineRow}
                >
                  {/* <br /> */}

                  <div className={Classes.lineSmall}>
                    <input
                      className={Classes.inputField}
                      placeholder={
                        r.kind === "fuel"
                          ? "Fuel description (optional)"
                          : r.kind === "discount"
                            ? "Discount description"
                            : "Item name"
                      }
                      value={r.description}
                      onChange={(e) =>
                        updateRow(i, {
                          description: e.target.value,
                        })
                      }
                    />
                  </div>
                  {/* <br /> */}

                  {r.kind === "fuel" ? (
                    <>
                      <div className={Classes.lineSmall}>
                        <select
                          className={Classes.inputField}
                          value={r.fuelTypeId}
                          onChange={(e) =>
                            updateRow(i, {
                              fuelTypeId: e.target.value,
                            })
                          }
                        >
                          <option value="reg">
                            Regular
                          </option>
                          <option value="mid">Diesel</option>
                          <option value="prem">
                            Premium
                          </option>
                        </select>
                      </div>
                      {/* <br /> */}

                      <div className={Classes.lineSmall}>
                        <input
                          type="number"
                          step="0.01"
                          className={Classes.inputField}
                          placeholder="Gallons"
                          value={r.quantity}
                          onChange={(e) =>
                            updateRow(i, {
                              quantity: e.target.value,
                            })
                          }
                        />
                      </div>
                      {/* <br /> */}

                      <div
                        className={Classes.lineSmall}
                        style={{
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                        }}
                      >
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!r.useOverride}
                            onChange={(e) => {
                              const next =
                                e.target.checked;
                              updateRow(i, {
                                useOverride: next,
                                overrideUnitPrice: next
                                  ? r.unitPrice ??
                                  (priceMap[
                                    r
                                      .fuelTypeId
                                  ] ?? 0)
                                  : r.overrideUnitPrice,
                              });
                            }}
                          />
                          <h5
                            style={{
                              fontSize: "12px",
                              whiteSpace:
                                "nowrap",
                            }}
                          >
                            Custom price
                          </h5>
                        </label>

                        {r.useOverride ? (
                          <input
                            type="number"
                            step="0.01"
                            className={
                              Classes.inputField
                            }
                            placeholder="Unit price"
                            value={
                              r.overrideUnitPrice
                            }
                            onChange={(e) =>
                              updateRow(i, {
                                overrideUnitPrice:
                                  e.target.value,
                              })
                            }
                            style={{ minWidth: 140 }}
                          />
                        ) : (
                          <input
                            className={
                              Classes.inputField
                            }
                            value={money(
                              priceMap[
                              r.fuelTypeId
                              ] ?? 0,
                              currencyCode
                            )}
                            readOnly
                            title="Auto from service"
                            style={{ minWidth: 140 }}
                          />
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={Classes.lineSmall}>
                        <input
                          type="number"
                          step="0.01"
                          className={Classes.inputField}
                          placeholder="Unit price"
                          value={r.unitPrice}
                          onChange={(e) =>
                            updateRow(i, {
                              unitPrice:
                                e.target.value,
                            })
                          }
                        />
                      </div>
                      {/* <br /> */}
                      <div className={Classes.lineSmall}>
                        <input
                          type="number"
                          step="0.01"
                          className={Classes.inputField}
                          placeholder="Qty"
                          value={r.quantity}
                          onChange={(e) =>
                            updateRow(i, {
                              quantity:
                                e.target.value,
                            })
                          }
                        />
                      </div>
                      {/* <br /> */}
                      <div className={Classes.lineSmall}>
                        <input
                          className={Classes.inputField}
                          value={money(
                            r.amount,
                            currencyCode
                          )}
                          readOnly
                        />
                      </div>
                    </>
                  )}

                  <div
                    className={Classes.lineActions}
                    style={{ marginTop: "5px" }}
                  >
                    <button
                      type="button"
                      className={Classes.addLineBtn}
                      onClick={() => removeRow(i)}
                      title="Remove"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="12"
                        viewBox="0 0 384 512"
                      >
                        <path d="M360 64H24C10.7 64 0 74.7 0 88V120C0 133.3 10.7 144 24 144H360C373.3 144 384 133.3 384 120V88C384 74.7 373.3 64 360 64zM64 192V456C64 486.5 89.5 512 120 512H264C294.5 512 320 486.5 320 456V192H64z" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}

              <div
                className="flexAlign10"
                style={{ gap: 10, marginTop: 8 }}
              >
                <button
                  type="button"
                  className={Classes.addLineBtn}
                  onClick={() => addRow("fuel")}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="12"
                    viewBox="0 0 448 512"
                  >
                    <path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 144L48 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l144 0 0 144c0 17.7 14.3 32 32 32s32-14.3 32-32l0-144 144 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-144 0 0-144z" />
                  </svg>
                  <h5>Add fuel</h5>
                </button>
              </div>
            </div>

            {/* Fees (editable when enabled) */}
            <div
              className={Classes.flexColGroup}
              style={{ marginTop: 16 }}
            >
              <div className={Classes.flexCol1}>
                <label className={Classes.label}>
                  <input
                    type="checkbox"
                    checked={includeDelivery}
                    onChange={(e) =>
                      setIncludeDelivery(
                        e.target.checked
                      )
                    }
                  />{" "}
                  Delivery Fee
                </label>
                <input
                  type="number"
                  step="0.01"
                  className={Classes.inputField}
                  disabled={!includeDelivery}
                  value={
                    includeDelivery
                      ? deliveryFeeAmount ?? ""
                      : ""
                  }
                  onChange={(e) =>
                    setDeliveryFeeAmount(
                      e.target.value
                    )
                  }
                  placeholder={defaultDelivery.toString()}
                />
              </div>
              <div className={Classes.flexCol1}>
                <label className={Classes.label}>
                  <input
                    type="checkbox"
                    checked={includeEmergency}
                    onChange={(e) =>
                      setIncludeEmergency(
                        e.target.checked
                      )
                    }
                  />{" "}
                  Emergency Fee
                </label>
                <input
                  type="number"
                  step="0.01"
                  className={Classes.inputField}
                  disabled={!includeEmergency}
                  value={
                    includeEmergency
                      ? emergencyFeeAmount ?? ""
                      : ""
                  }
                  onChange={(e) =>
                    setEmergencyFeeAmount(
                      e.target.value
                    )
                  }
                  placeholder={defaultEmergency.toString()}
                />
              </div>
            </div>

            {/* Totals */}
            <div
              className={Classes.flexColGroup}
              style={{ marginTop: 8 }}
            >
              <div className={Classes.flexCol1}>
                <label className={Classes.label}>
                  Subtotal
                </label>
                <input
                  className={Classes.inputField}
                  readOnly
                  value={money(subtotal, currencyCode)}
                />
              </div>
              <div className={Classes.flexCol1}>
                <label className={Classes.label}>
                  Total
                </label>
                <input
                  className={Classes.inputField}
                  readOnly
                  value={money(total, currencyCode)}
                />
              </div>
            </div>

            <div className={Classes.flexEnd}>
              <button
                type="submit"
                className={Classes.submitBtn}
                disabled={loading}
              >
                <h5>
                  {loading
                    ? isEdit
                      ? "Saving…"
                      : "Creating…"
                    : isEdit
                      ? "Save changes"
                      : "Add invoice"}
                </h5>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
